import { useState } from 'react';
import Layout from '@/components/Layout';
import { ChevronDown, ChevronUp, Zap, Hand } from 'lucide-react';

export default function TreatmentsPage() {
  const [expandedCondition, setExpandedCondition] = useState(null);

  const treatments = [
    {
      title: "Dry Needling",
      image: "/images/Dry needling.png",
      description: "Dry needling targets trigger points in muscles to relieve pain and improve mobility. Fine needles are inserted into specific points to release muscle tension and promote healing.",
      objectPosition: "center"
    },
    {
      title: "Joint Manipulation",
      image: "/images/Joint manipulation.jpg",
      description: "Joint manipulation involves skilled movements applied to joints to restore normal motion, reduce pain, and improve overall function.",
      objectPosition: "bottom"
    },
    {
      title: "Kinesio Taping",
      image: "/images/Kinesio taping.jpg",
      description: "Kinesio taping uses elastic therapeutic tape to support muscles and joints while allowing full range of motion, reducing pain and swelling.",
      objectPosition: "center"
    },
    {
      title: "Manual Manipulation",
      image: "/images/Manipulation.jpg",
      description: "Manual manipulation techniques help restore joint mobility, reduce muscle tension, and alleviate pain through hands-on therapeutic approaches.",
      objectPosition: "center"
    },
    {
      title: "Muscular Pain Treatment",
      image: "/images/Muscular pain.jpg",
      description: "Comprehensive treatment for muscular pain including soft tissue mobilization, stretching, and strengthening exercises to address the root cause of discomfort.",
      objectPosition: "bottom"
    },
    {
      title: "Post-Operative Rehabilitation",
      image: "/images/Post Op.jpg",
      description: "Specialized rehabilitation programs designed to help patients recover optimally after surgery, restoring function and preventing complications.",
      objectPosition: "center"
    }
  ];

  const otherTreatments = [
    {
      title: "Electrotherapy",
      icon: <Zap className="w-6 h-6" />,
      description: "Uses electrical stimulation to reduce pain, decrease swelling, and promote tissue healing through various modalities including TENS and ultrasound therapy."
    },
    {
      title: "Myofascial Release",
      icon: <Hand className="w-6 h-6" />,
      description: "A hands-on technique that applies gentle sustained pressure to the myofascial connective tissue to eliminate pain and restore motion."
    }
  ];

  const conditions = [
    {
      title: "Neurological Conditions",
      subtitle: "Stroke, ABI/TBI, GBS, MS, CP",
      abbreviations: [
        { abbr: "ABI/TBI", meaning: "Acquired Brain Injury / Traumatic Brain Injury" },
        { abbr: "GBS", meaning: "Guillain-Barré Syndrome" },
        { abbr: "MS", meaning: "Multiple Sclerosis" },
        { abbr: "CP", meaning: "Cerebral Palsy" }
      ]
    },
    {
      title: "Cardiothoracic Post-Operative Conditions",
      subtitle: "CABG, AVR, TAVI, etc",
      abbreviations: [
        { abbr: "CABG", meaning: "Coronary Artery Bypass Grafting" },
        { abbr: "AVR", meaning: "Aortic Valve Replacement" },
        { abbr: "TAVI", meaning: "Transcatheter Aortic Valve Implantation" }
      ]
    },
    {
      title: "Post-Op Rehabilitation",
      subtitle: "Back, Neck, UL and LL (trauma)",
      abbreviations: [
        { abbr: "Back", meaning: "Spinal or vertebral injuries" },
        { abbr: "Neck", meaning: "Cervical spine injuries" },
        { abbr: "UL", meaning: "Upper Limb (shoulder, arm, wrist, hand)" },
        { abbr: "LL", meaning: "Lower Limb (hip, knee, ankle, foot)" }
      ]
    }
  ];

  const otherConditions = [
    "Muscular and joint pains and aches",
    "Chest conditions - nebulization and manual chest physiotherapy",
    "Pre-natal exercises, posture corrections, back pain",
    "Post-natal exercises"
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4 animate-fade-in" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Treatments
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl">
            Comprehensive physiotherapy services tailored to your recovery needs
          </p>
        </div>
      </section>

      {/* Treatments Grid - Turquoise Background */}
      <section className="py-16 bg-[#5ce1e6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {treatments.map((treatment, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100 card-hover animate-fade-in treatment-card"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`treatment-card-${index}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src={treatment.image} 
                    alt={treatment.title}
                    className="w-full h-full object-cover treatment-image"
                    style={{ objectPosition: treatment.objectPosition || 'center' }}
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {treatment.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {treatment.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Other Treatments - White Background */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-800 mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Additional Treatments
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {otherTreatments.map((treatment, index) => (
              <div 
                key={index}
                className="bg-slate-50 rounded-2xl p-6 shadow-sm border border-slate-100 flex items-start gap-4 card-hover animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-12 h-12 rounded-full bg-[#f0fdfd] flex items-center justify-center flex-shrink-0 text-[#0e7490]">
                  {treatment.icon}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {treatment.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {treatment.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Conditions We Treat - Light Grey Background */}
      <section className="py-16 bg-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Conditions We Treat
          </h2>
          
          {/* Expandable Conditions */}
          <div className="space-y-4 mb-8">
            {conditions.map((condition, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm"
              >
                <button
                  onClick={() => setExpandedCondition(expandedCondition === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                  data-testid={`condition-expand-${index}`}
                >
                  <div>
                    <h3 className="font-semibold text-slate-900">{condition.title}</h3>
                    <p className="text-sm text-slate-700">{condition.subtitle}</p>
                  </div>
                  {expandedCondition === index ? (
                    <ChevronUp className="w-5 h-5 text-slate-600" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-600" />
                  )}
                </button>
                {expandedCondition === index && (
                  <div className="px-6 pb-4 border-t border-slate-200 bg-white">
                    <div className="pt-4 space-y-2">
                      {condition.abbreviations.map((item, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <span className="font-medium text-slate-900 min-w-[60px]">{item.abbr}</span>
                          <span className="text-slate-700">– {item.meaning}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Other Conditions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {otherConditions.map((condition, index) => (
              <div 
                key={index}
                className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 shadow-sm"
              >
                <div className="w-2 h-2 rounded-full bg-[#0e7490]" />
                <span className="text-slate-900">{condition}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
